import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  User, 
  LogOut, 
  Calendar, 
  FileText, 
  CreditCard, 
  Settings,
  Menu,
  X,
  Home,
  Users,
  BarChart3,
  FolderOpen,
  MessageCircle,
  Cookie,
  ClipboardCheck,
  BookOpen,
  ClipboardList,
  Search,
  Mail,
  ScrollText,
  Bell,
  Wallet,
  Ruler,
  CalendarDays,
  Clock,
  Wrench,
  LayoutGrid,
  Euro,
  Tag,
  Key,
  Fingerprint,
  FilePenLine
} from 'lucide-react';
import { useState, useEffect } from 'react';
import ChatWidget from './ChatWidget';
import CookieBanner from './CookieBanner';

export function Header() {
  const { user, logout, isAdmin, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 glass-header border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="header-logo">
            <picture>
              <source srcSet="/logo-new.webp" type="image/webp" />
              <img 
                src="/logo-new.png" 
                alt="FarbWelt Maler Express Service Logo" 
                className="h-12 sm:h-14 w-auto object-contain"
                width="806"
                height="256"
                fetchPriority="high"
                decoding="sync"
              />
            </picture>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <Link 
              to="/" 
              className={`font-manrope text-sm font-medium transition-colors ${
                isActive('/') ? 'text-[#9B1B30]' : 'text-gray-600 hover:text-[#1A1A1A]'
              }`}
              data-testid="nav-home"
            >
              Startseite
            </Link>

            <Link 
              to="/termin" 
              className={`font-manrope text-sm font-medium transition-colors ${
                isActive('/termin') ? 'text-[#9B1B30]' : 'text-gray-600 hover:text-[#1A1A1A]'
              }`}
              data-testid="nav-booking"
            >
              Termin buchen
            </Link>
            {isAuthenticated && !isAdmin && (
              <Link 
                to="/dashboard" 
                className={`font-manrope text-sm font-medium transition-colors ${
                  isActive('/dashboard') ? 'text-[#9B1B30]' : 'text-gray-600 hover:text-[#1A1A1A]'
                }`}
                data-testid="nav-dashboard"
              >
                Mein Bereich
              </Link>
            )}
            {isAdmin && (
              <Link 
                to="/admin" 
                className={`font-manrope text-sm font-medium transition-colors ${
                  location.pathname.startsWith('/admin') ? 'text-[#9B1B30]' : 'text-gray-600 hover:text-[#1A1A1A]'
                }`}
                data-testid="nav-admin"
              >
                Admin
              </Link>
            )}
          </nav>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-8 h-8 bg-[#2E2A88] rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-manrope text-sm text-gray-700">
                      {user?.first_name}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2 border-b">
                    <p className="font-manrope font-medium text-sm">{user?.first_name} {user?.last_name}</p>
                    <p className="font-manrope text-xs text-gray-500">{user?.email}</p>
                    {user?.customer_number && (
                      <p className="font-manrope text-xs text-[#9B1B30] font-medium mt-1">
                        {user.customer_number}
                      </p>
                    )}
                  </div>
                  {!isAdmin && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                        <Home className="w-4 h-4 mr-2" />
                        Übersicht
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/dashboard/termine')} data-testid="menu-appointments">
                        <Calendar className="w-4 h-4 mr-2" />
                        Meine Termine
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/dashboard/dokumente')} data-testid="menu-documents">
                        <FileText className="w-4 h-4 mr-2" />
                        Dokumente
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/dashboard/zahlungen')} data-testid="menu-payments">
                        <CreditCard className="w-4 h-4 mr-2" />
                        Zahlungen
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/einstellungen')} data-testid="menu-settings">
                    <Settings className="w-4 h-4 mr-2" />
                    Einstellungen
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="font-manrope" data-testid="login-btn">
                    Anmelden
                  </Button>
                </Link>
                <Link to="/register">
                  <Button variant="outline" className="font-manrope" data-testid="register-btn">
                    Registrieren
                  </Button>
                </Link>
                <Link to="/termin">
                  <Button className="btn-primary" data-testid="cta-booking-btn">
                    Termin buchen
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t animate-fadeIn">
            <nav className="flex flex-col gap-3">
              <Link 
                to="/" 
                className="font-manrope text-sm font-medium text-gray-600 hover:text-[#1A1A1A] py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Startseite
              </Link>

              <Link 
                to="/termin" 
                className="font-manrope text-sm font-medium text-gray-600 hover:text-[#1A1A1A] py-2 border-t pt-3 mt-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Termin buchen
              </Link>
              {isAuthenticated && !isAdmin && (
                <Link 
                  to="/dashboard" 
                  className="font-manrope text-sm font-medium text-gray-600 hover:text-[#1A1A1A] py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Mein Bereich
                </Link>
              )}
              {isAdmin && (
                <Link 
                  to="/admin" 
                  className="font-manrope text-sm font-medium text-gray-600 hover:text-[#1A1A1A] py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Admin
                </Link>
              )}
              <div className="pt-3 border-t mt-2">
                {isAuthenticated ? (
                  <Button 
                    variant="ghost" 
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="w-full justify-start text-red-600"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Abmelden
                  </Button>
                ) : (
                  <div className="flex flex-col gap-2">
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full">Anmelden</Button>
                    </Link>
                    <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full">Registrieren</Button>
                    </Link>
                    <Link to="/termin" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="btn-primary w-full">Termin buchen</Button>
                    </Link>
                  </div>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="bg-[#1A1A1A] text-white py-8">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <picture>
                <source srcSet="/logo-new.webp" type="image/webp" />
                <img 
                  src="/logo-new.png" 
                  alt="FarbWelt Maler Express Service Logo" 
                  className="h-16 w-auto object-contain bg-white rounded-lg p-1.5"
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </div>
            <p className="font-manrope text-sm text-gray-400">
              Termin- & Kundenportal
            </p>
          </div>

          {/* Kontakt */}
          <div>
            <h4 className="font-barlow font-bold text-sm uppercase tracking-wider mb-3">Kontakt</h4>
            <ul className="space-y-1 font-manrope text-sm text-gray-400">
              <li>Tel: 02133 7787688</li>
              <li>kontakt@farbwelt-service.de</li>
              <li className="pt-2">
                <a 
                  href="https://www.farbwelt-service.de" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#9B1B30] hover:underline"
                >
                  www.farbwelt-service.de
                </a>
              </li>
            </ul>
          </div>

          {/* Rechtliches */}
          <div>
            <h4 className="font-barlow font-bold text-sm uppercase tracking-wider mb-3">Rechtliches</h4>
            <ul className="space-y-1 font-manrope text-sm text-gray-400">
              <li><Link to="/datenschutz" className="hover:text-white transition-colors">Datenschutz</Link></li>
              <li><Link to="/impressum" className="hover:text-white transition-colors">Impressum</Link></li>
              <li><Link to="/agb" className="hover:text-white transition-colors">AGB</Link></li>
              <li>
                <button 
                  onClick={() => {
                    localStorage.removeItem('farbwelt_cookie_consent');
                    window.location.reload();
                  }}
                  className="hover:text-white transition-colors flex items-center gap-1"
                >
                  <Cookie className="w-3 h-3" />
                  Cookie-Einstellungen
                </button>              </li>
            </ul>
          </div>
        </div>

        {/* Programmatisches SEO: Einsatzgebiete (additiv, verlinkt statische Landingpages) */}
        <div className="border-t border-gray-800 mt-8 pt-6">
          <h4 className="font-barlow font-bold text-sm uppercase tracking-wider mb-3 text-gray-300">Unsere Einsatzgebiete</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 font-manrope text-xs text-gray-400">
            <a href="/malerarbeiten/koeln" className="hover:text-white">Malerarbeiten Köln</a>
            <a href="/malerarbeiten/duesseldorf" className="hover:text-white">Malerarbeiten Düsseldorf</a>
            <a href="/malerarbeiten/neuss" className="hover:text-white">Malerarbeiten Neuss</a>
            <a href="/malerarbeiten/dormagen" className="hover:text-white">Malerarbeiten Dormagen</a>
            <a href="/malerarbeiten/leverkusen" className="hover:text-white">Malerarbeiten Leverkusen</a>
            <a href="/malerarbeiten/monheim" className="hover:text-white">Malerarbeiten Monheim</a>
            <a href="/malerarbeiten/grevenbroich" className="hover:text-white">Malerarbeiten Grevenbroich</a>
            <a href="/malerarbeiten/meerbusch" className="hover:text-white">Malerarbeiten Meerbusch</a>
            <a href="/tapezierarbeiten/koeln" className="hover:text-white">Tapezierarbeiten Köln</a>
            <a href="/tapezierarbeiten/duesseldorf" className="hover:text-white">Tapezierarbeiten Düsseldorf</a>
            <a href="/tapezierarbeiten/neuss" className="hover:text-white">Tapezierarbeiten Neuss</a>
            <a href="/tapezierarbeiten/dormagen" className="hover:text-white">Tapezierarbeiten Dormagen</a>
            <a href="/bodenverlegung/koeln" className="hover:text-white">Bodenverlegung Köln</a>
            <a href="/bodenverlegung/duesseldorf" className="hover:text-white">Bodenverlegung Düsseldorf</a>
            <a href="/bodenverlegung/neuss" className="hover:text-white">Bodenverlegung Neuss</a>
            <a href="/bodenverlegung/dormagen" className="hover:text-white">Bodenverlegung Dormagen</a>
            <a href="/renovierung/koeln" className="hover:text-white">Renovierung Köln</a>
            <a href="/renovierung/duesseldorf" className="hover:text-white">Renovierung Düsseldorf</a>
            <a href="/renovierung/neuss" className="hover:text-white">Renovierung Neuss</a>
            <a href="/renovierung/dormagen" className="hover:text-white">Renovierung Dormagen</a>
            <a href="/wohnungsuebergabe/koeln" className="hover:text-white">Wohnungsübergabe Köln</a>
            <a href="/wohnungsuebergabe/duesseldorf" className="hover:text-white">Wohnungsübergabe Düsseldorf</a>
            <a href="/wohnungsuebergabe/neuss" className="hover:text-white">Wohnungsübergabe Neuss</a>
            <a href="/wohnungsuebergabe/dormagen" className="hover:text-white">Wohnungsübergabe Dormagen</a>
            <a href="/trockenbau/koeln" className="hover:text-white">Trockenbau Köln</a>
            <a href="/trockenbau/duesseldorf" className="hover:text-white">Trockenbau Düsseldorf</a>
            <a href="/trockenbau/neuss" className="hover:text-white">Trockenbau Neuss</a>
            <a href="/trockenbau/dormagen" className="hover:text-white">Trockenbau Dormagen</a>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-6 pt-6 flex flex-col md:flex-row justify-between items-center gap-2">
          <p className="font-manrope text-xs text-gray-500">
            © {new Date().getFullYear()} FarbWelt Maler Express Service
          </p>
        </div>
      </div>
    </footer>
  );
}

export function AdminSidebar({ isOpen, onClose }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tenantBranding, setTenantBranding] = useState(null);

  const isMainAdmin = user?.role === 'admin' && (user?.tenant_id === 'main' || !user?.tenant_id);
  const isTenantAdmin = user?.role === 'admin' && user?.tenant_id && user?.tenant_id !== 'main';

  // For tenant admins: load their own document_settings to show their company branding in the sidebar
  useEffect(() => {
    if (!isTenantAdmin) return;
    fetch(`/api/admin/documents/settings`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) setTenantBranding({
          companyName: d.company_name || `${user.first_name} ${user.last_name}`,
          logoUrl: d.logo_url ? (d.logo_url.startsWith('http') ? d.logo_url : d.logo_url) : null,
        });
      })
      .catch(() => {});
  }, [isTenantAdmin, user?.first_name, user?.last_name]);

  // Permission mapping from menu path to permission key
  const permissionMap = {
    '/admin': 'dashboard',
    '/admin/kunden': 'customers',
    '/admin/termine': 'appointments',
    '/admin/dateien': 'files',
    '/admin/dokumente': 'documents',
    '/admin/briefe': 'documents',
    '/admin/protokolle': 'protocols',
    '/admin/chats': 'chats',
    '/admin/mitarbeiter': 'employees',
    '/admin/einstellungen': 'settings',
    '/admin/anpassungen': 'settings', // Site customization needs settings permission
  };

  const allMenuItems = [
    { path: '/admin', icon: BarChart3, label: 'Dashboard', exact: true },
    { path: '/admin/auftraege', icon: Search, label: 'Auftrags-Finder' },
    { path: '/admin/kunden', icon: Users, label: 'Kunden' },
    { path: '/admin/termine', icon: Calendar, label: 'Termine' },
    { path: '/admin/dateien', icon: FolderOpen, label: 'Dateien' },
    { path: '/admin/dokumente', icon: FileText, label: 'FarbWelt Office' },
    { path: '/admin/briefe', icon: ScrollText, label: 'Briefe' },
    { path: '/admin/vertraege', icon: FilePenLine, label: 'Verträge' },
    { path: '/admin/gutachten', icon: ClipboardList, label: 'Gutachten' },
    { path: '/admin/protokolle', icon: ClipboardCheck, label: 'Abnahmeprotokoll' },
    { path: '/admin/chats', icon: MessageCircle, label: 'Kundenservice' },
    { path: '/admin/mitarbeiter', icon: Users, label: 'Mitarbeiter', adminOnly: true },
    { path: '/admin/anpassungen', icon: Settings, label: 'Anpassungen', adminOnly: true },
    { path: '/admin/aufmass', icon: Ruler, label: 'Aufmaß', adminOnly: true },
    { path: '/admin/grundriss', icon: LayoutGrid, label: 'Grundriss-Analyse', adminOnly: true },
    { path: '/admin/einsatzplanung', icon: CalendarDays, label: 'Einsatzplanung', adminOnly: true },
    { path: '/admin/zeiterfassung', icon: Clock, label: 'Stempeluhr', adminOnly: true },
    { path: '/admin/werkzeug', icon: Wrench, label: 'Werkzeug & Lager', adminOnly: true },
    { path: '/admin/zahlungserinnerungen', icon: Bell, label: 'Zahlungserinnerungen', adminOnly: true },
    { path: '/admin/mitarbeiter-zahlungen', icon: Wallet, label: 'Mitarbeiter-Zahlungen', adminOnly: true },
    { path: '/admin/lohnabrechnung', icon: Euro, label: 'Lohnabrechnung', adminOnly: true },
    { path: '/admin/rabattcodes', icon: Tag, label: 'Rabattcodes', adminOnly: true },
    { path: '/admin/lizenzen', icon: Key, label: 'Lizenzen', adminOnly: true, mainAdminOnly: true },
    { path: '/admin/postausgang', icon: Mail, label: 'Postausgang', adminOnly: true },
    { path: '/admin/anleitung', icon: BookOpen, label: 'Anleitung' },
    { path: '/admin/einstellungen', icon: Settings, label: 'Einstellungen' },
  ];

  // Permission mapping for licensed tenants
  // Maps frontend path → license_permissions key
  const licensedPermMap = {
    '/admin': 'documents',           // Dashboard – needs at least one permission
    '/admin/dokumente': 'documents',
    '/admin/briefe': 'documents',
    '/admin/vertraege': 'contracts',
    '/admin/termine': 'calendar',
    '/admin/kunden': 'customers',
    '/admin/gutachten': 'gutachten',
    '/admin/auftraege': 'lead_finder',
    '/admin/rabattcodes': 'discount_codes',
    '/admin/postausgang': 'emails',
    '/admin/einstellungen': 'settings',
    '/admin/anpassungen': 'settings',
  };

  // Filter menu items based on user role and permissions
  const menuItems = allMenuItems.filter(item => {
    // Main admin (FarbWelt) sees everything
    if (isMainAdmin) return true;
    
    // Tenant admins (licensed): filter strictly by license_permissions
    if (isTenantAdmin) {
      if (item.mainAdminOnly) return false;
      // Always-on items for tenant admins (Dashboard / Einstellungen / Anpassungen)
      const alwaysOn = ['/admin', '/admin/einstellungen', '/admin/anpassungen'];
      if (alwaysOn.includes(item.path)) {
        return true;
      }
      const permKey = licensedPermMap[item.path];
      if (!permKey) return false; // Unmapped paths are hidden by default
      return user?.permissions?.[permKey] === true;
    }
    
    // Admin-only items are hidden for employees
    if (item.adminOnly) return false;
    
    // Check employee permissions
    if (user?.role === 'employee' && user?.permissions) {
      const permKey = permissionMap[item.path];
      return permKey ? user.permissions[permKey] : false;
    }
    
    return false;
  });

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleNavClick = () => {
    if (onClose) onClose();
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white border-r border-gray-200 h-screen flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="p-4 lg:p-6 border-b flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2 lg:gap-3" onClick={handleNavClick}>
            {isTenantAdmin && tenantBranding?.logoUrl ? (
              <img 
                src={tenantBranding.logoUrl}
                alt={tenantBranding.companyName || 'Firmenlogo'}
                className="h-12 lg:h-14 w-auto object-contain"
              />
            ) : isTenantAdmin ? (
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-gradient-to-br from-[#9B1B30] to-[#2E2A88] flex items-center justify-center text-white font-bold text-lg">
                  {(tenantBranding?.companyName || user?.first_name || 'M')[0].toUpperCase()}
                </div>
                <div className="font-barlow font-bold text-sm text-[#1A1A1A] leading-tight max-w-[140px] truncate">
                  {tenantBranding?.companyName || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Mein Portal'}
                </div>
              </div>
            ) : (
              <picture>
                <source srcSet="/logo-new.webp" type="image/webp" />
                <img 
                  src="/logo-new.png" 
                  alt="FarbWelt Maler Express Service Logo" 
                  className="h-12 lg:h-14 w-auto object-contain"
                  fetchPriority="high"
                  decoding="sync"
                />
              </picture>
            )}
          </Link>
          {/* Close button for mobile */}
          <button 
            className="lg:hidden p-2 text-gray-500 hover:text-gray-700"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 lg:p-4 overflow-y-auto">
          <ul className="space-y-1">
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  onClick={handleNavClick}
                  className={`flex items-center gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-md font-manrope text-sm transition-colors ${
                    isActive(item.path, item.exact)
                      ? 'bg-[#9B1B30] text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid={`admin-nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* User Section */}
        <div className="p-3 lg:p-4 border-t">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 lg:w-10 lg:h-10 bg-[#2E2A88] rounded-full flex items-center justify-center">
              <User className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
            </div>
            <div>
              <p className="font-manrope font-medium text-xs lg:text-sm">{user?.first_name} {user?.last_name}</p>
              <p className="font-manrope text-xs text-gray-500">Administrator</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-red-600 hover:bg-red-50 text-sm"
            onClick={() => { logout(); navigate('/'); }}
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Abmelden
          </Button>
        </div>
      </aside>
    </>
  );
}

export function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <ChatWidget />
      <CookieBanner />
    </div>
  );
}

export function AdminLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const isTenantAdmin = user?.role === 'admin' && user?.tenant_id && user?.tenant_id !== 'main';
  
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  return (
    <div className="min-h-screen flex bg-gray-50">
      <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-md"
              data-testid="admin-mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/admin" className="flex items-center gap-2">
              {isTenantAdmin ? (
                <span className="font-barlow font-bold text-lg text-[#1A1A1A]">Admin</span>
              ) : (
                <>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_color-booking/artifacts/ympcjgqw_erasebg-transformed.svg" 
                    alt="FarbWelt Logo" 
                    className="w-8 h-8 object-contain"
                  />
                  <span className="font-barlow font-bold text-lg text-[#1A1A1A]">Admin</span>
                </>
              )}
            </Link>
          </div>
          {/* Mobile Logout Button */}
          <button 
            onClick={handleLogout}
            className="p-2 text-red-600 hover:bg-red-50 rounded-md"
            data-testid="admin-mobile-logout-btn"
            title="Abmelden"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </header>
        <main className="flex-1 p-4 lg:p-8 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
